"use client";

import { Globe, ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useI18n, type Locale } from "@/i18n/I18nProvider";

const menuLinks = [
  { labelKey: "Medical Services", href: "/services" },
  { labelKey: "Hospitals", href: "/hospitals" },
  { labelKey: "Doctors", href: "/doctors" },
  { labelKey: "Book a Service", href: "/book" },
  { labelKey: "Travel Assistance", href: "/travel" },
  { labelKey: "Help Center", href: "/help" },
];

// First two codes map to real locales; rest are visual placeholders
const languages: Array<{ code: string; labelKey: string; locale?: Locale }> = [
  { code: "EN", labelKey: "English", locale: "en" },
  { code: "中文", labelKey: "简体中文", locale: "zh" },
  { code: "繁體", labelKey: "繁體中文" },
  { code: "日本", labelKey: "日本語" },
  { code: "한국", labelKey: "한국어" },
  { code: "FR", labelKey: "Français" },
  { code: "DE", labelKey: "Deutsch" },
  { code: "ES", labelKey: "Español" },
];

export default function Header() {
  const { locale, setLocale, t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [displayCode, setDisplayCode] = useState("EN");
  const langRef = useRef<HTMLDivElement | null>(null);

  // Keep display code in sync with current locale
  useEffect(() => {
    setDisplayCode(locale === "zh" ? "中文" : "EN");
  }, [locale]);

  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [langOpen]);

  return (
    <div className="relative z-[60]">
      <header className="relative flex items-center justify-between px-5 pt-3.5 pb-2.5 bg-white">
        <Link href="/" className="flex items-center gap-2.5">
          <svg width="30" height="30" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <circle cx="16" cy="9.2" r="6.4" fill="#0F6B68" />
            <circle cx="22.8" cy="16" r="6.4" fill="#0F6B68" />
            <circle cx="16" cy="22.8" r="6.4" fill="#0F6B68" />
            <circle cx="9.2" cy="16" r="6.4" fill="#0F6B68" />
            <circle cx="16" cy="16" r="7.2" fill="#0F6B68" />
            <circle cx="13.4" cy="7.6" r="2.2" fill="#2FA79B" fillOpacity="0.6" />
          </svg>
          <div>
            <p className="text-[19px] font-extrabold text-primary leading-none tracking-tight">
              ChinaCare<span className="text-primary-light">+</span>
            </p>
            <p className="text-[11px] text-text-dark/55 leading-none mt-1.5">{t("Medical Care in China")}</p>
          </div>
        </Link>

        <div className="flex items-center gap-1">
          <div ref={langRef} className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1 px-1.5 py-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 text-[11.5px] font-medium text-text-dark transition-colors"
              aria-label="Language selector"
              aria-expanded={langOpen}
              aria-haspopup="menu"
            >
              <Globe size={15} className="text-primary" />
              <span>{displayCode}</span>
              <ChevronDown
                size={12}
                className={`text-text-dark/45 -ml-0.5 transition-transform ${langOpen ? "rotate-180" : ""}`}
              />
            </button>
            {langOpen && (
              <div
                role="menu"
                className="absolute right-0 top-[calc(100%+6px)] w-[170px] rounded-xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.12)] border border-[#EDF1F1] py-1.5 z-50"
              >
                {languages.map((l) => (
                  <button
                    key={l.code}
                    role="menuitem"
                    onClick={() => {
                      if (l.locale) setLocale(l.locale);
                      setDisplayCode(l.code);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-[12.5px] transition-colors ${
                      displayCode === l.code
                        ? "text-primary font-semibold bg-primary-bg/60"
                        : "text-text-dark hover:bg-gray-50"
                    }`}
                  >
                    <span>{t(l.labelKey)}</span>
                    <span className="text-[10.5px] font-mono text-text-dark/40">{l.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setMenuOpen((v) => !v);
              setLangOpen(false);
            }}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-200 text-text-dark/60 transition-colors"
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Slide menu */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-[70]"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed top-0 right-0 w-[260px] h-full bg-white z-[80] shadow-xl pt-16 px-5">
            <button
              onClick={() => setMenuOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"
              aria-label="Close menu"
            >
              <X size={16} />
            </button>
            <nav className="space-y-1">
              {menuLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 px-3 rounded-xl text-sm font-medium text-text-dark hover:bg-primary-bg hover:text-primary transition-colors"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
